
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { InfoIcon, MailIcon, CheckCircle, AlertCircle, Loader2 } from "lucide-react";

export const DirectSmtpTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    details?: any;
  } | null>(null);

  const sendTestEmail = async () => {
    try {
      setLoading(true);
      setResult(null);
      
      const { data, error } = await supabase.functions.invoke("smtp-direct-test", {
        method: "POST"
      });
      
      if (error) {
        console.error("Edge function error:", error);
        toast({
          variant: "destructive",
          title: "Test Failed",
          description: `Edge function error: ${error.message || "Unknown error"}`
        });
        
        setResult({
          success: false,
          message: `Edge function error: ${error.message || "Unknown error"}`
        });
        return;
      }
      
      if (!data.success) {
        toast({
          variant: "destructive",
          title: "Email Test Failed",
          description: data.message || "Failed to send test email"
        });
        
        setResult({
          success: false,
          message: data.message || "Failed to send test email",
          details: data.details || data.error
        });
        return;
      }
      
      toast({
        variant: "success",
        title: "Email Sent",
        description: "Test email has been sent successfully"
      });
      
      setResult({
        success: true,
        message: data.message || "Test email sent successfully",
        details: data.details
      });
    } catch (error: any) {
      console.error("Error sending test email:", error);
      toast({
        variant: "destructive",
        title: "Test Failed",
        description: `Error: ${error.message || "Unknown error"}`
      });
      
      setResult({
        success: false,
        message: `Error: ${error.message || "Unknown error"}`
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Direct SMTP Test</h3>
          <p className="text-sm text-muted-foreground">
            Send a test email using SMTP configuration from Supabase edge secrets
          </p>
        </div>
        <Button 
          onClick={sendTestEmail}
          disabled={loading}
          className="flex items-center gap-2"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MailIcon className="h-4 w-4" />
          )}
          {loading ? "Sending..." : "Send Test Email"}
        </Button>
      </div>
      
      {result && (
        <Alert variant={result.success ? "info" : "destructive"} className="mt-4">
          {result.success ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertTitle>{result.success ? "Success" : "Error"}</AlertTitle>
          <AlertDescription className="mt-2">
            <p>{result.message}</p>
            {result.details && (
              <pre className="mt-2 p-2 bg-gray-100 text-xs rounded overflow-auto max-h-[200px]">
                {JSON.stringify(result.details, null, 2)}
              </pre>
            )}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
